import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {lastDayOfMonth, format} from "date-fns";
import {observer} from "mobx-react-lite";
import PaginatedDataTable from "../../molecules/paginatedDataTable/paginatedDataTable";
import Occasion from "../../../stores/domainStore/pages/models/occasion";
import {ColumnDescription} from "react-bootstrap-table-next";
import {SortOrder} from "../../../shared/enums/sortOrder";
import classNames from "classnames";
import Icon from "../../atoms/common/icon/icon";
import {IconSource} from "../../../shared/enums/iconSource";
import "./specialDaysDataList.sass";
import {ObservableList} from "../../../stores/observables/observableList";
import {OptionMenuKeyValue} from "../../../stores/observables/optionMenuKeyValue";
import OptionMenu from "../../atoms/optionMenu/optionMenu";
import {useStores} from "../../../stores/useStores";
import SpecialDaysDetailModal from "../specialDaysDetailModal/specialDaysDetailModal";
import {MultiVillageHelper} from "../../../shared/multiVillageHelper";
import {useHistory} from "react-router";
import {RoutePaths} from "../../../shared/routePaths";
import {MultiVillageSelectObservableList} from "../../../stores/observables/multiVillageSelectObservableList";
import HeaderProgressBar from "../../atoms/headerProgressBar/headerProgressBar";
import {compareDates} from "../../../utils/sortUtils/sortUtil";
import {ToastMessageStatus} from "../../atoms/toastMessage/toastMessage";
import NoResultsIcon from "../../atoms/noResultsIcon/noResultsIcon";
import {AuthUser} from "../../../utils/auth/user";
import {allVillageKeyValue} from "../../../shared/constants";
import {Navigation} from "../../../utils/navigation";

const SpecialDaysDataList: React.FC = () => {
  const history = useHistory();

  const {t} = useTranslation(["translation", "specialDays"]);
  const {
    uiStore: {occasionUiStore, modalStore, toastMessageStore},
    domainStore: {occasionDomainStore}
  } = useStores();
  const [sortField, setSortField] = useState<string>("date");
  const [selectedSpecialDay, setSelectedSpecialDay] = useState<Occasion | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const sortedOccasions = useMemo(() => {
    return sortOccasions(
      occasionDomainStore.occasions,
      occasionUiStore.sortOrder
    );
  }, [occasionDomainStore.occasions, occasionUiStore.sortOrder]);

  useEffect(() => {
    const controller = new AbortController();

    const getSpecialDays = async () => {
      try {
        setIsLoading(true);
        await occasionDomainStore.getOccasions(
          `${occasionUiStore.calendarYear}-01-01`,
          format(
            lastDayOfMonth(new Date(occasionUiStore.calendarYear, 11, 1)),
            "yyyy-MM-dd"
          ),
          undefined,
          controller
        );
        setIsLoading(false);
      } catch {
        if (controller.signal.aborted) return;
        toastMessageStore.displayToastMessage(
          t("specialDays:messages.generalErrorMessage"),
          ToastMessageStatus.Danger
        );
        setIsLoading(false);
      }
    };

    getSpecialDays();

    return () => {
      controller.abort();
    };
  }, [occasionUiStore, occasionUiStore.calendarYear]);

  const canSort = sortedOccasions.length > 0;

  const navigateToEditSpecialDay = (specialDayId: string) => {
    Navigation.navigateWithSelectedVillageId(
      history,
      `${RoutePaths.SpecialDays}/edit/${specialDayId}`
    );
  };

  const headerLabelFormatter = (column: ColumnDescription<any, any>) => {
    const isColumnSorting = sortField === column.dataField;
    const isSortAscending = occasionUiStore.sortOrder === SortOrder.Ascending;
    return (
      <div
        className={classNames("special-days-table-sortable-header", {
          "is-sort": isColumnSorting
        })}
      >
        {column.text}
        {canSort && column.dataField === "date" && (
          <div>
            {isColumnSorting && isSortAscending && (
              <Icon source={IconSource.toggleUp} />
            )}
            {isColumnSorting && !isSortAscending && (
              <Icon source={IconSource.toggleDown} />
            )}
          </div>
        )}
      </div>
    );
  };

  const dateFormatter = (_: any, template: Occasion, __: any) => {
    return template.date.date ? format(template.date.date, "dd/MM/yyyy") : "";
  };

  const cateringFormatter = (_: any, template: Occasion, __: any) => {
    return template.getCateringLabel(t);
  };

  const specialDayHeaderFormatter = (column: ColumnDescription<any, any>) => {
    return (
      <div className="special-day-title-label">
        <Icon source={IconSource.favourite} />
        <span>{column.text}</span>
      </div>
    );
  };

  const specialDayTitleFormatter = (_: any, template: Occasion, __: any) => {
    return (
      <div
        className="special-day-col"
        onClick={(_) => {
          setSelectedSpecialDay(template);
          setIsModalOpen(true);
        }}
      >
        <div>
          {template.isPrimary.value ? (
            <Icon source={IconSource.favourite} />
          ) : null}
        </div>
        <span
          className={
            template.isPrimary.value ? "with-isprimary" : "without-isprimary"
          }
        >
          {template.title.text}
        </span>
      </div>
    );
  };

  const deleteSpecialDayAsync = async (
    specialDayId: string,
    specialDayTitle: string
  ) => {
    try {
      setIsLoading(true);
      await occasionDomainStore.deleteOccasion(specialDayId);
      toastMessageStore.displayToastMessage(
        t("specialDays:messages.deletedSpecialDay", {
          occasionTitle: specialDayTitle
        }),
        ToastMessageStatus.Success
      );
    } catch {
      toastMessageStore.displayToastMessage(
        t("specialDays:messages.deletedSpecialDayFailure", {
          eventTitle: specialDayTitle
        }),
        ToastMessageStatus.Danger
      );
    } finally {
      setIsLoading(false);
    }
  };

  const optionMenuFormatter = (_: any, occasion: Occasion) => {
    const actions = [
      new OptionMenuKeyValue("edit", t("translation:actions:edit"), false),
      new OptionMenuKeyValue("delete", t("translation:actions:delete"), true)
    ];
    return (
      <OptionMenu
        options={new ObservableList<OptionMenuKeyValue>(actions)}
        onOptionSelected={(selectedObservableItem: OptionMenuKeyValue) => {
          switch (selectedObservableItem.value) {
            case "edit": {
              navigateToEditSpecialDay(occasion.id);
              break;
            }
            case "delete": {
              modalStore.openModalWithContent({
                content: t("specialDays:messages.deleteNotice"),
                title: t("specialDays:titles.deleteNotice"),
                primaryButtonText: t(
                  "translation:confirmations.affirmativeDelete"
                ),
                centered: true,
                dangerButton: true,
                onPrimaryClick: () => {
                  deleteSpecialDayAsync(occasion.id, occasion.title.text);
                  modalStore.toggle();
                }
              });
              break;
            }
            default:
              break;
          }
        }}
        disabled={false}
      />
    );
  };

  const villageFormatter = (villageIds: MultiVillageSelectObservableList) => {
    if (villageIds.selectedItems.length === 0) return allVillageKeyValue.label;

    const villagesByCountry = AuthUser.getInstance().villagesByCountry;

    const selectedVillages = villageIds.selectedItems.map((v) => v.label);

    const villagesString = MultiVillageHelper.multiVillageString(
      selectedVillages,
      villagesByCountry
    );

    return MultiVillageHelper.VillageEllipsis(
      t,
      villagesString !== "" ? [villagesString] : selectedVillages,
      true,
      3
    );
  };

  const columns: ColumnDescription<any, any>[] = [
    {
      dataField: "id",
      hidden: true,
      text: "Id"
    },
    {
      dataField: "date",
      text: t("specialDays:labels.date"),
      classes: "date-col",
      sort: canSort,
      headerFormatter: headerLabelFormatter,
      formatter: dateFormatter
    },
    {
      dataField: "title",
      text: t("specialDays:labels.specialDay"),
      classes: "special-day-title-col",
      sort: false,
      headerFormatter: specialDayHeaderFormatter,
      formatter: specialDayTitleFormatter
    },
    {
      dataField: "villageIds",
      text: t("specialDays:labels.village"),
      classes: "village-col",
      sort: false,
      headerFormatter: headerLabelFormatter,
      formatter: villageFormatter
    },
    {
      dataField: "catering",
      text: t("specialDays:labels.catering"),
      classes: "catering-col",
      sort: false,
      headerFormatter: headerLabelFormatter,
      formatter: cateringFormatter
    },
    {
      dataField: "optionMenu",
      text: "",
      sort: false,
      classes: "option-menu-col",
      formatter: optionMenuFormatter
    }
  ];

  const sortByDate = () => {
    if (occasionDomainStore.occasions.length === 0) return;

    const newSortOrder =
      occasionUiStore.sortOrder === SortOrder.Ascending
        ? SortOrder.Descending
        : SortOrder.Ascending;

    occasionUiStore.updateSortOrder(newSortOrder);
  };

  function sortOccasions(
    unsortedSpecialDays: Occasion[],
    sortOrder: SortOrder
  ) {
    return Array.from(unsortedSpecialDays).sort((a, b) => {
      return compareDates(a.date.date, b.date.date, sortOrder);
    });
  }

  return (
    <div className="special-days-list" data-testid="specialDaysList">
      {selectedSpecialDay && (
        <SpecialDaysDetailModal
          specialDayDetail={selectedSpecialDay}
          isOpen={isModalOpen}
          toggle={() => setIsModalOpen(!isModalOpen)}
          onEditClick={() => navigateToEditSpecialDay(selectedSpecialDay.id)}
        />
      )}
      <HeaderProgressBar progressBarLoading={isLoading} />
      {sortedOccasions.length === 0 && isLoading === false ? (
        <div className="no-special-days">
          <NoResultsIcon
            titleText={`${t("specialDays:messages.noSearchResults", {
              year: occasionUiStore.calendarYear
            })}`}
          />
        </div>
      ) : (
        <PaginatedDataTable
          data={sortedOccasions}
          keyField="id"
          columns={columns}
          sizePerPage={9999}
          onSort={sortByDate}
          remote={true}
          noBorderBottom={true}
        />
      )}
    </div>
  );
};

export default observer(SpecialDaysDataList);
